import { z } from 'zod';
import { AdminProvidersSummary } from './admin-providers.js';
import { CONSENT_KINDS, HEARD_FROMS, LIVING_SITUATIONS, PROVINCES, USER_STATUSES } from './enums.js';
import { MemberAddress } from './members.js';
import { IsoDateTime, MemberNumber } from './primitives.js';
/** AuditLog actions on a user that the member page shows as account history. */
export const ADMIN_USER_ACTIONS = ['user.banned', 'user.unbanned', 'user.sessions_revoked'];
/** Columns the member directory can sort by. */
export const ADMIN_MEMBER_SORTS = ['memberSince', 'lastName', 'memberNumber'];
export const ADMIN_MEMBER_PAGE_SIZES = [25, 50, 100];
/**
 * `GET /admin/members`, and the back office's URL search params for the
 * same list, so every view of the directory is shareable by URL
 * (apps/admin/CLAUDE.md). Every field falls back to its default instead of
 * failing: an old or hand-edited link still opens a list.
 */
export const AdminMemberListQuery = z.object({
    /** Name, email, member number or phone number. Words narrow the search. */
    q: z.string().trim().max(100).optional().catch(undefined),
    /** The province of the member's main address. */
    province: z.enum(PROVINCES).optional().catch(undefined),
    /** Whether the member entered an estate agent's referral code. */
    referred: z.enum(['yes', 'no']).optional().catch(undefined),
    /** The account status: `active` is ACTIVE, `banned` is SUSPENDED (the back office calls it a ban). */
    status: z.enum(['active', 'banned']).optional().catch(undefined),
    sort: z.enum(ADMIN_MEMBER_SORTS).default('memberSince').catch('memberSince'),
    dir: z.enum(['asc', 'desc']).default('desc').catch('desc'),
    page: z.coerce.number().int().min(1).default(1).catch(1),
    pageSize: z.coerce
        .number()
        .pipe(z.union(ADMIN_MEMBER_PAGE_SIZES.map((size) => z.literal(size))))
        .default(25)
        .catch(25),
});
/** One row of the member directory. `phone` is E.164. */
export const AdminMemberRow = z.object({
    memberNumber: MemberNumber,
    firstName: z.string(),
    lastName: z.string(),
    email: z.string(),
    emailVerified: z.boolean(),
    phone: z.string(),
    status: z.enum(USER_STATUSES),
    memberSince: IsoDateTime,
    /** Where the main address is. Null only if the member has no address. */
    mainAddress: z
        .object({
        suburb: z.string().nullable(),
        city: z.string().nullable(),
        province: z.enum(PROVINCES),
    })
        .nullable(),
    addressCount: z.int(),
    heardFrom: z.enum(HEARD_FROMS),
    referralCode: z.string().nullable(),
});
/** 200 from `GET /admin/members`: one page of the directory and the total that matched. */
export const AdminMemberList = z.object({
    items: z.array(AdminMemberRow),
    total: z.int(),
    page: z.int(),
    pageSize: z.int(),
});
/** 200 from `GET /admin/members/{memberNumber}`: everything the back office shows about one member. */
export const AdminMemberDetail = z.object({
    /** For the admin actions on the user: ban, unban, end sessions. */
    userId: z.string(),
    memberNumber: MemberNumber,
    firstName: z.string(),
    lastName: z.string(),
    email: z.string(),
    emailVerified: z.boolean(),
    phone: z.string(),
    memberSince: IsoDateTime,
    livingSituation: z.enum(LIVING_SITUATIONS),
    heardFrom: z.enum(HEARD_FROMS),
    referralCode: z.string().nullable(),
    status: z.enum(USER_STATUSES),
    banReason: z.string().nullable(),
    /** When the member last signed in, or null if they never have. */
    lastLoginAt: IsoDateTime.nullable(),
    /** Main address first. */
    addresses: z.array(MemberAddress),
    /** Admin actions on the account (ban, unban, end sessions), newest first, from the audit log. */
    history: z.array(z.object({
        action: z.enum(ADMIN_USER_ACTIONS),
        reason: z.string().nullable(),
        at: IsoDateTime,
        /** Null when the admin's account has since been removed. */
        actorName: z.string().nullable(),
    })),
    /** Every consent decision, newest first: the POPIA record. */
    consents: z.array(z.object({
        kind: z.enum(CONSENT_KINDS),
        version: z.string(),
        granted: z.boolean(),
        at: IsoDateTime,
    })),
});
/** How many members the overview lists under "Newest members". */
export const ADMIN_OVERVIEW_RECENT_MEMBERS = 5;
/**
 * 200 from `GET /admin/overview`: the numbers on the back office's first
 * screen. Jobs and savings join it with their features.
 */
export const AdminOverview = z.object({
    members: z.object({
        total: z.int(),
        /** Joined in the 7 days up to now. */
        joinedLast7Days: z.int(),
        /** Newest first. */
        recent: z.array(AdminMemberRow),
    }),
    providers: AdminProvidersSummary,
});
//# sourceMappingURL=admin-members.js.map